import { createSlice } from '@reduxjs/toolkit';

// Cross-slice listener: floorPlanSlice.addFloorPlan/addFloorPlans action'larını
// import etmeden type ile dinleriz (circular import'tan kaçınmak için).
const ADD_FLOOR_PLAN_TYPE = 'floorPlans/addFloorPlan';
const ADD_FLOOR_PLANS_TYPE = 'floorPlans/addFloorPlans';

// Hardcoded fallbacks (used when backend config is not yet loaded)
export const PROJECT_TABS = [
  { id: 'mimari',   label: 'Mimari',              icon: '🏛️', suffix: '' },
  { id: 'elektrik', label: 'Elektrik Kuvvetli Akım', icon: '⚡', suffix: '-E' },
  { id: 'telekom',  label: 'Telekom',              icon: '📡', suffix: '-T' },
  { id: 'yangin',   label: 'Yangın Algılama',      icon: '🔥', suffix: '-Y' },
  { id: 'santiye',  label: 'Şantiye',              icon: '🏗️', suffix: '-S' },
  { id: 'baglanti', label: 'Bağlantı Hattı',       icon: '🔌', suffix: '-B' },
];

export const USER_ROLES = [
  'Müellif',
  'Yapı Denetim',
  'Kontrolör',
  'Dağıtım Kurumu',
  'Belediye',
];

// Fallback PAFTA_OPTIONS — will be replaced by backend sheet types
export const PAFTA_OPTIONS = [
  { id: 'elektrik', label: 'Kuvvetli Akım' },
  { id: 'telekom',  label: 'Telekom' },
  { id: 'yangin',   label: 'Yangın Algılama' },
  { id: 'santiye',  label: 'Şantiye' },
  { id: 'baglanti', label: 'Bağlantı Hattı' },
];

// Legacy (hardcoded) → backend code mapping
export const LEGACY_TO_CODE = {
  elektrik: 'strong_current',
  telekom: 'weak_current',
  yangin: 'fire_detection',
  santiye: 'construction_site',
  baglanti: 'connection_line',
};

/**
 * Legacy tab ID'yi mevcut tab sistemindeki gerçek ID'ye çözümle.
 * Backend yüklenmişse backend code'u, yoksa legacy ID'yi döner.
 */
export const resolveTabId = (tabs, legacyId) => {
  if (tabs.some(t => t.id === legacyId)) return legacyId;
  const code = LEGACY_TO_CODE[legacyId];
  if (code && tabs.some(t => t.id === code)) return code;
  return legacyId;
};

// Sheet code → tab icon/suffix mapping
const SHEET_ICON_MAP = {
  strong_current:     { icon: '⚡', suffix: '-E' },
  weak_current:       { icon: '📡', suffix: '-T' },
  fire_detection:     { icon: '🔥', suffix: '-Y' },
  construction_site:  { icon: '🏗️', suffix: '-S' },
  connection_line:    { icon: '🔌', suffix: '-B' },
};

const initialState = {
  tabs: PROJECT_TABS,
  activeTabId: 'mimari',
  enabledPaftaIds: ['mimari'],
  userRole: 'Müellif',
  // Backend-derived pafta options (null until loaded)
  paftaOptions: null,
};

const projectTabsSlice = createSlice({
  name: 'projectTabs',
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTabId = action.payload;
    },
    /**
     * Proje yüklemesinde kaydedilen tab state'ini toptan restore et.
     * @param action.payload - { activeTabId, enabledPaftaIds, userRole? }
     */
    restoreTabState: (state, action) => {
      const { activeTabId, enabledPaftaIds, userRole } = action.payload || {};
      if (activeTabId) state.activeTabId = activeTabId;
      if (Array.isArray(enabledPaftaIds)) {
        // mimari her zaman dahil
        if (!enabledPaftaIds.includes('mimari')) enabledPaftaIds.unshift('mimari');
        state.enabledPaftaIds = enabledPaftaIds;
      }
      if (userRole) state.userRole = userRole;
    },
    /**
     * Tüm sekmeleri kapat, sadece mimari kal. DXF açılırken veya yeni proje başlarken kullanılır.
     */
    resetTabs: (state) => {
      state.activeTabId = 'mimari';
      state.enabledPaftaIds = ['mimari'];
    },
    setUserRole: (state, action) => {
      state.userRole = action.payload;
    },
    togglePafta: (state, action) => {
      const id = action.payload;
      if (id === 'mimari') return; // mimari her zaman açık
      const idx = state.enabledPaftaIds.indexOf(id);
      if (idx === -1) {
        state.enabledPaftaIds.push(id);
        state.activeTabId = id;
      } else {
        state.enabledPaftaIds.splice(idx, 1);
        if (state.activeTabId === id) {
          state.activeTabId = 'mimari';
        }
      }
    },
    /**
     * Set tabs and pafta options from backend sheet types.
     * @param action.payload - Array of { id, code, name, order }
     */
    setTabsFromSheets: (state, action) => {
      const sheets = action.payload;
      if (!Array.isArray(sheets) || sheets.length === 0) return;

      // "Mimari" tab is always first and always present
      const mimariTab = { id: 'mimari', label: 'Mimari', icon: '🏛️', suffix: '' };
      const newTabs = [mimariTab];
      const newPaftaOptions = [];

      sheets.forEach(sheet => {
        const mapping = SHEET_ICON_MAP[sheet.code] || { icon: '📄', suffix: `-${sheet.code.charAt(0).toUpperCase()}` };
        newTabs.push({
          id: sheet.code,
          label: sheet.name,
          icon: mapping.icon,
          suffix: mapping.suffix,
        });
        newPaftaOptions.push({
          id: sheet.code,
          label: sheet.name,
        });
      });

      state.tabs = newTabs;
      state.paftaOptions = newPaftaOptions;

      // Keep enabledPaftaIds that still exist in new tabs
      const validIds = new Set(newTabs.map(t => t.id));
      state.enabledPaftaIds = state.enabledPaftaIds.filter(id => validIds.has(id));
      if (!state.enabledPaftaIds.includes('mimari')) {
        state.enabledPaftaIds.unshift('mimari');
      }
      // If active tab no longer valid, switch to mimari
      if (!validIds.has(state.activeTabId)) {
        state.activeTabId = 'mimari';
      }
    },
    /**
     * Set party types as user roles from backend.
     * @param action.payload - Array of { id, code, name }
     */
    setRolesFromParties: (state, action) => {
      // Roles will be driven from editorConfig, not stored here
      // This is kept for potential future use
    },
  },
  extraReducers: (builder) => {
    // Plan eklendiğinde temel 3 paftayı (elektrik, telekom, yangin) otomatik
    // enabledPaftaIds'e ekle. Sebep: floorPlanSlice.addFloorPlan reducer'ı
    // bu paftaların tabs'ını oluşturuyor ve plan'ları kopyalıyor — ama
    // projectTabs.enabledPaftaIds güncellenmiyordu. Sonuç: tesisat çizilmeden
    // kayıt edilen projeler reload'da sadece mimari paftası ile açılıyordu
    // (save akışı enabledPaftaIds'i okuyor, listede sadece 'mimari' bulunca
    // tek paftalı save ediyor).
    const enableDefaultPaftas = (state) => {
      ['elektrik', 'telekom', 'yangin'].forEach(id => {
        if (!state.enabledPaftaIds.includes(id)) {
          state.enabledPaftaIds.push(id);
        }
      });
    };
    builder.addCase(ADD_FLOOR_PLAN_TYPE, enableDefaultPaftas);
    builder.addCase(ADD_FLOOR_PLANS_TYPE, enableDefaultPaftas);
  },
});

export const { setActiveTab, setUserRole, togglePafta, setTabsFromSheets, setRolesFromParties, restoreTabState, resetTabs } = projectTabsSlice.actions;
export default projectTabsSlice.reducer;
